import React, { useState, useEffect, useRef, useCallback, useContext, useMemo } from "react";
import { submitFormData } from "../../../../services/APIRequest";
import { serializeForm } from "../../../../helpers/FormSerializer";
import { OrganizationContext } from "context/OrganizationContext";
import { useParseHTML } from "hooks/useParseHTML";
import styles from "../DynamicForm.module.scss";

import RenderItemsByType from "./RenderItemsByType";
import ContactInformationSection from "./ContactInformationSection";
import Button from "components/elements/Button/Button";
import { LoggerContext } from "context/LoggerContext";
import FixedContainer from "components/elements/FixedContainer/FixedContainer";
import confetti from "utils/confetti";

import Section from "components/Section";

// Debug configuration flag - set to false in production
const debugLogs = false;

/**
 * Multi-step form component
 * Manages form state and validation across multiple steps including real-time validation
 */
const MultiStepForm = ({ data }) => {

	// Get organization context and logger
	const organization = useContext(OrganizationContext);
	const { logDebug } = useContext(LoggerContext);

	// Parse policy text with the hook (moved outside render function)
	const parsedPolicyText = useParseHTML(data.policy_text || 'Ich erkläre mich mit der Verarbeitung meiner Angaben zum Zweck der Bearbeitung einverstanden.');

	// Helper debug logging function
	const debug = useCallback((message, ...data) => {
		if (debugLogs) {
			console.log(`[MultiStepForm Debug] ${message}`, ...data);
			logDebug(message, ...data);
		}
	}, [logDebug]);

	// Filter active steps and their active items - wrapped in useMemo to prevent recreation on every render
	const activeSteps = useMemo(() => {
		// First filter active steps
		// Filter only active steps and sort by sort_number (ascending), then by id (ascending), treating null/undefined as 0
		const filteredSteps = data.steps
			? data.steps.filter((step) => step.active === 1)
			: [];

		const sortedSteps = filteredSteps.sort((a, b) => {
			const aSort = a.sort_number != null ? parseInt(a.sort_number, 10) : 0;
			const bSort = b.sort_number != null ? parseInt(b.sort_number, 10) : 0;
			if (aSort !== bSort) {
				return aSort - bSort;
			}
			const aId = a.id != null ? parseInt(a.id, 10) : 0;
			const bId = b.id != null ? parseInt(b.id, 10) : 0;
			return aId - bId;
		});

		// Then filter active items within each step
		return sortedSteps.map(step => {
			// Create a shallow copy of the step to avoid mutating the original data
			const stepCopy = { ...step };

			// If items array exists, filter it to only include active items or items without an active property
			if (Array.isArray(stepCopy.items)) {
				stepCopy.items = stepCopy.items.filter(item =>
					item.active === undefined || item.active === 1
				);
			}

			return stepCopy;
		});
	}, [data.steps]);

	// Form state
	const [currentStep, setCurrentStep] = useState(0);

	useEffect(() => {console.log("currentStep",currentStep)}, [currentStep]);

	// State for validation and selection
	const [stepValidationState, setStepValidationState] = useState({});
	const [isSubmitted, setIsSubmitted] = useState(false);


	// Ref for form and inputs
	const formRef = useRef(null);
	const inputRefs = useRef({});

	// Determine contact details position
	const contactDetailsAreFirstStep = data.contact_details_first === 1;
	const leadContactType = data.lead_contact || 1;
	const askForContactInformation = leadContactType > 1;
	const enableAnonymousOption = data.enable_anonymous_option === 1;

	// Calculate total steps and contact details step number based on position preference
	const leadStepNumber = askForContactInformation ? (contactDetailsAreFirstStep ? 1 : activeSteps.length + 1) : null;
	const totalSteps = activeSteps.length + 1; // +1 for policy step (which may include contact)

	// Wrap allSteps in useMemo to prevent unnecessary recreation
	const allSteps = useMemo(() => {
		let steps = [{ type: "introduction", step: false }, ...activeSteps.map((step) => ({ type: "normal", step: step }))];

		if (askForContactInformation) {
			const leadStep = { type: "lead", step: false };
			steps.splice(leadStepNumber, 0, leadStep);
		}
		
		return steps;
	}, [activeSteps, askForContactInformation, leadStepNumber]);

	// Helper to scroll to and focus an element
	const scrollToElement = useCallback((element) => {
		if (!element) return;
		
		setTimeout(() => {
			element.scrollIntoView({
				behavior: "smooth",
				block: "center",
			});

			if (element.focus && typeof element.focus === "function") {
				element.focus();

				// Special handling for select elements
				if (element.tagName === "SELECT") {
					try {
						const selectControl = element.closest(".form-group, .mb-3")?.querySelector(".select__control");
						if (selectControl) selectControl.click();
					} catch (e) {
						logDebug("Could not focus select element", e);
					}
				}
			}
		}, 100);
	}, [logDebug]);

	
	/**
	 * Resets all validation error UI elements within a container
	 * Removes error classes for the specified container
	 * @param {HTMLElement} container - The container element to clear errors in (usually a step element)
	 */
	const clearUiValidationErrors = useCallback((container) => {
		if (!container) return;

		const inputParent = container.type === 'hidden' ? container?.parentElement?.parentElement?.parentElement :container?.parentElement?.parentElement;
		
		// Remove is-invalid class from all elements
		const invalidFields = inputParent.querySelectorAll(".is-invalid");

		invalidFields.forEach((field) => field.classList.remove("is-invalid"));
		
		// Remove error class from container
		container.classList.remove("step-has-error");
		
		// Remove validation error container classes
		const validationErrorContainers = inputParent.querySelectorAll(".has-validation-error");
		validationErrorContainers.forEach((errorContainer) => {
			errorContainer.classList.remove("has-validation-error");
		});
		
		debug(`Validation errors cleared for container`, container);
	}, [debug]);

	/**
	 * Marks an input element (or its container) as invalid and applies error styling
	 * @param {HTMLElement} input - The input element with validation error
	 * @param {string} errorMessage - Optional error message to display
	 * @param {number} stepIndex - Optional step index for state updates
	 * @returns {Object} Object with relevant error elements for tracking
	 */
	const setUiValidationError = useCallback((input) => {
		if (!input) return { targetElement: null };
		
		// Check if input is hidden (display: none or type="hidden")
		const isHidden = window.getComputedStyle(input).display === 'none' || input.type === "hidden";
		const inputParent = input.parentElement;
		const inputParentParent = inputParent.parentElement;
		
		
		// For hidden inputs, mark the parent
		if (isHidden && inputParent) {
			inputParent.classList.add("is-invalid");
			if (inputParentParent) {
				inputParentParent.classList.add("has-validation-error");;
			}
			
		} else {
			input.classList.add("is-invalid");
			if (inputParent) {
				inputParent.classList.add("has-validation-error");;
			}
		}

		
		
		
		// For special input types like checkboxes and radios, 
		// find their container and mark it as invalid too
		const specialContainer = input.closest(".form-check, .checkbox, .radio-option");
		if (specialContainer) {
			specialContainer.classList.add("is-invalid");
			
			// For radio button groups, mark all related labels for clearer visual feedback
			if (input.type === "radio") {
				const radioGroup = document.querySelectorAll(`input[name="${input.name}"]`);
				radioGroup.forEach(radio => {
					const radioLabel = radio.closest('label');
					if (radioLabel) radioLabel.classList.add("has-validation-error");
				});
			}
		}
		
		
		// Get input identifier for logging
		const inputIdentifier = input.name || input.id || input.type || 'unknown';
		
		debug(`Marked ${inputIdentifier} as invalid`);
		
	}, [debug]);
	

	const validateInput = useCallback((input, step_number, reportValidity = false) => {
		    let nokey = false	
			if (!input) return true;
			if (!input.dataset?.serialKey) {
				console.warn("No data-serial-key found for input", input);
				nokey = true
			};
			const inputKey = input.dataset.serialKey ?? "NO_KEY";

			function isInputValid(input) {
				if (input.type === 'hidden') {
					if (input.required) {
						return input.value.trim().length > 0;
					}
				  	return true; // Wenn nicht required, dann immer gültig
				} else {
				  	return input.checkValidity();
				}
			}


			// Check if the input is valid using the browser's native validation
			const isValid = isInputValid(input);
			if (isValid) {
				clearUiValidationErrors(input);
			} else {
				setUiValidationError(input);	
			}

			if (nokey) return isValid;
			
			if (!isValid) {
				setStepValidationState((prevState) => ({
					...prevState,
					[step_number]: {
						...prevState[step_number],
						[inputKey]: input.validationMessage
					}
				}));
				
			} else {
				setStepValidationState((prevState) => {
					const parent = prevState[step_number];
					// Check if the parent exists and has the specific input
					if (!parent || !parent[inputKey]) return prevState;
					// Remove the specific input from the validation state
					const { [inputKey]: removed, ...remainingChildren } = parent;
	
					return {
						...prevState,
						[step_number]: remainingChildren,
					};
				}
				);
			}
			return isValid;
		}, [clearUiValidationErrors, setUiValidationError, setStepValidationState]);

	const validateMinMaxCount = useCallback((input, step_number) => {
		let isValid = true;
		if (!input) return true;
		let counterWrapper = input.closest(".selection-counter-wrapper");
		if (!counterWrapper) {
			const counterWrapperChilds = input.querySelectorAll(".selection-counter-wrapper");
			if (counterWrapperChilds && counterWrapperChilds.length > 0) {
				counterWrapper = counterWrapperChilds[0];
			}
		}
		if (!counterWrapper) return true;
		if (!counterWrapper.dataset?.serialKey) {
			console.warn("No data-serial-key found for counterWrapper", counterWrapper);
			return true;
		}

		const counterKey = counterWrapper.dataset?.serialKey;
		const validationStatus = counterWrapper.dataset?.validationStatus || "valid";

		if (validationStatus === "invalid") {
			isValid = false;
			setStepValidationState((prevState) => ({
				...prevState,
				[step_number]: {
					...prevState[step_number],
					[counterKey]: validationStatus
				}
			}));
		} else {
			setStepValidationState((prevState) => {
				const parent = prevState[step_number];
				// Check if the parent exists and has the specific input
				if (!parent || !(counterKey in parent)) return prevState;
				// Remove the specific input from the validation state
				const { [counterKey]: removed, ...remainingChildren } = parent;

				return {
					...prevState,
					[step_number]: remainingChildren,
				};
			});
		}

		return isValid;
	}, []);

	const showValidationError = (input) => {
		if (!input) return;
		input.reportValidity();
	}

	//useEffect(() => {console.log(stepValidationState) }, [stepValidationState]);

	const validateCurrentStep = useCallback((specificStepIndex = null) => {

		// Determine which step to validate
		const stepToValidate = specificStepIndex !== null ? specificStepIndex : currentStep;
		const stepData = allSteps[stepToValidate];

		// Skip validation for introduction steps
		if (stepData?.type === "introduction") {
			debug(`Step ${stepToValidate}: Skipping validation for introduction step`);
			return true;
		}

		// Get the step element using the stepToValidate index
		const stepElement = document.querySelector(`[data-step="${stepToValidate}"]`);
		if (!stepElement) {
			debug(`Step ${stepToValidate}: No DOM element found, skipping validation`);
			return true;
		}

		debug(`Step ${stepToValidate}: Validating step of type ${stepData?.type || "normal"}`);

		let isValid = true;

		const requiredInputs = stepElement.querySelectorAll("input, select, textarea");

		let firstError = true;

		requiredInputs.forEach((input) => {
			// Validate each input and update the isValid flag
			const inputIsValid = validateInput(input, stepToValidate);
			if (!inputIsValid) {
				isValid = false;
				setUiValidationError(input);
				if (firstError){
					firstError = false;
					scrollToElement(input);
					showValidationError(input);
				}
			}
		});

		

		const minMaxValidation = validateMinMaxCount(stepElement, stepToValidate);

		if (!minMaxValidation) {
			isValid = false;
		}
		
		// If validation is successful, clear any previous validation errors for this step
		if (isValid) {
			debug(`Step ${stepToValidate}: All validations passed successfully`);
		} else {
			debug(`Step ${stepToValidate}: Validation failed`);
		}


		return isValid;

	},[currentStep, allSteps, debug, setUiValidationError, validateInput , validateMinMaxCount, scrollToElement]);
	
	// Improved validation function for all steps - now uses validateCurrentStep for each step
	const validateFormCompletely = useCallback(() => {

		debug("Starting complete form validation for all steps");
		let isFormValid = true;

		allSteps.forEach((stepData, index) => {
			const stepValidation = validateCurrentStep(index);
			if (!stepValidation) {
				isFormValid = false;
			}
		})
		
		if (debugLogs){
			debug("result:", isFormValid)
			return false;
		} else {
			return isFormValid;
		}
		
	}, [allSteps, validateCurrentStep, debug]);

	const handleMinMaxChange = useCallback((e) => {

		const input = e?.target;
		const validity = e?.detail?.validity
		const serialKey = input?.dataset?.serialKey;
		const step = e.detail?.step;

		if (!input) return;
		if (validity !== true && validity !== false) return;
		if (!serialKey) return;
		if (!step) return;

		const step_number = (allSteps.findIndex(item => item.step?.id === step.id) ?? -1) ;
		if (!step_number === -1 ) return;

		if (currentStep !== step_number) return;

		const counterKey = serialKey;

		if (validity) {
			setStepValidationState((prevState) => {
				const parent = prevState[step_number];
				// Check if the parent exists and has the specific input
				if (!parent || !(counterKey in parent)) return prevState;
				// Remove the specific input from the validation state
				const { [counterKey]: removed, ...remainingChildren } = parent;
				
				return {
					...prevState,
					[step_number]: remainingChildren,
				};
			})
		} else {
			setStepValidationState((prevState) => ({
				...prevState,
				[step_number]: {
					...prevState[step_number],
					[counterKey]: validity
				}
			}));
		} 
		

	}, [setStepValidationState, allSteps, currentStep]);

	// Improved function for handling input changes
	const handleInputChange = useCallback((e) => {
		const input = e.target;

		if (!input) return;

		validateInput(input, currentStep);
		showValidationError(input);
	}, [validateInput, currentStep]);

	// Add event listeners for input changes
	useEffect(() => {
		const form = formRef.current;
		if (form) {
			// Global listener for input changes
			const handleGlobalInputChange = (e) => {
				if (e.target.tagName === "INPUT" || e.target.tagName === "SELECT" || e.target.tagName === "TEXTAREA") {
					handleInputChange(e);
				}
			};

			form.addEventListener("input", handleGlobalInputChange);
			form.addEventListener("change", handleGlobalInputChange);

			return () => {
				form.removeEventListener("input", handleGlobalInputChange);
				form.removeEventListener("change", handleGlobalInputChange);
			};
		}
	}, [handleInputChange]);


	// Add event listeners for input changes
	useEffect(() => {
		const form = formRef.current;
		if (form) {
			form.addEventListener("validity-changed", handleMinMaxChange);

			return () => {
				form.removeEventListener("change", handleMinMaxChange);
			};
		}
	}, [handleMinMaxChange]);

	

	// Navigate to next step with validation
	const goToNextStep = useCallback(() => {
		// Validate current step first
		const isCurrentStepValid = validateCurrentStep();

		if (!isCurrentStepValid) {
			return; // Stay on current step if validation fails
		}

		// If we're on the introduction step (step 0), go to step 1 or contact step if it's first
		if (currentStep === 0) {
			setCurrentStep(contactDetailsAreFirstStep && askForContactInformation ? 1 : 1);
		}
		
		// If we're on a main content step
		else if (currentStep > 0 && currentStep <= activeSteps.length) {
			// Special case for contact step if it's first
			if (contactDetailsAreFirstStep && askForContactInformation && currentStep === 1) {
				setCurrentStep(2); // Go to first content step
			} else {
				// Go to next step, or policy
				const nextStep = currentStep + 1;

				// If we're at the end of content steps and contact details are at the end
				if (nextStep > activeSteps.length && askForContactInformation && !contactDetailsAreFirstStep) {
					setCurrentStep(leadStepNumber);
				} else {
					setCurrentStep(nextStep);
				}
			}
		}
		// If we're on contact step and it's at the end
		else if (currentStep === leadStepNumber && !contactDetailsAreFirstStep && askForContactInformation) {
			setCurrentStep(totalSteps);
		}
		// Otherwise, just increment
		else {
			setCurrentStep(currentStep + 1);
		}

		// Scroll to top after step change
		window.scrollTo({ top: 0, behavior: "smooth" });
	}, [currentStep, totalSteps, contactDetailsAreFirstStep, askForContactInformation, activeSteps.length, leadStepNumber, validateCurrentStep]);

	// Go back to previous step
	const goToPreviousStep = useCallback(() => {
		// If we're on step 1 or contact step when it's first, go to introduction
		if (currentStep === 1 || (currentStep === leadStepNumber && contactDetailsAreFirstStep && askForContactInformation)) {
			setCurrentStep(0);
		}
		// If we're on the policy step (last step)
		else if (currentStep === totalSteps) {
			// If contact is at the end, go to the last content step
			if (askForContactInformation && !contactDetailsAreFirstStep) {
				setCurrentStep(activeSteps.length);
			}
			// If contact is at the beginning or there is no contact, go to contact step or policy
			else {
				setCurrentStep(totalSteps - 1);
			}
		}
		// If we're on contact step and it's at the end
		else if (currentStep === leadStepNumber && !contactDetailsAreFirstStep && askForContactInformation) {
			setCurrentStep(activeSteps.length);
		}
		// Otherwise, just decrement
		else {
			setCurrentStep(currentStep - 1);
		}

		// Scroll to top after step change
		window.scrollTo({ top: 0, behavior: "smooth" });
	}, [currentStep, totalSteps, contactDetailsAreFirstStep, askForContactInformation, activeSteps.length, leadStepNumber]);

	// Form submission handler
	const handleSubmit = async (e) => {
		e.preventDefault();

		// Check if form validation passes
		const isFormValid = validateFormCompletely();
		if (isFormValid) {
			try {
				setIsSubmitted(true);

				const formElement = e.target;
				const formData = serializeForm(formElement, data);
				const suffix = data.lead_suffix || "ticket/leads/public";
				
				// Submit form data
				await submitFormData(suffix, formData, organization?.otk);
				//alert("Formular erfolgreich gesendet!");

				// Start confetti with timeout of 2000ms (2 seconds)
				const confettiController = confetti();
				confettiController.start(2000);

				// After successful submission reload the page after confetti
				setTimeout(() => {
					window.location.reload();
				}, 2000);
			} catch (error) {
				setIsSubmitted(false); // Reset on error
				alert("Error submitting form: " + error.message);
			}
		}
		// For invalid form, validateFormCompletely has already scrolled to the error
	};

	// Function to save refs for input elements
	const setInputRef = (name, ref) => {
		if (ref) {
			inputRefs.current[name] = ref;
		}
	};

	// Monitor showValidationErrors changes
	/*useEffect(() => {
		console.log('showValidationErrors state updated:', showValidationErrors);
	}, [showValidationErrors]);*/



	const renderStep = (stepData, stepIndex) => {
		const step = stepData.step || {};

		const renderIntroductionStep = () => (
			<Section organization={organization} section={{
						global_type:{ id: 1, name: "HeroSection" },
						title: data.title,
						short_desc: data.short_desc,
						desc: data.desc,
						title_vertical_alignment: "center",
						media: data.medias || data.media || [],

						alignment : "start",
						alignment_content: "end",
						alignment_title: "start",
						padding_y: 8,
						col_width: 12,
						col_width_content: (data.medias || data.media).length > 0? 5 : 0,
						col_width_title: (data.medias || data.media)?.length > 0? 6 : 12,

						
						vertical_alignment_title: "start",
						order: "forward",
						text_alignment_title: "start",
						vertical_alignment_content: "center",

			}} />
		);

		const renderLeadStep = (contactType) => (
			<>
				<ContactInformationSection
					leadContactType={leadContactType}
					setInputRef={setInputRef}
					enableAnonymousOption={enableAnonymousOption}
				/>
			</>
		);

		const renderPolicyStep = () => (
			<>
				<div className="mb-4">
					<h4 className="mb-3">Bestätigen und Absenden</h4>
					<p>Bitte überprüfen Sie Ihre Angaben und senden Sie das Formular ab.</p>
				</div>

				<div className="mt-5">
					<div className="form-check policy-checkbox">
						<input
							className="form-check-input"
							type="checkbox"
							id="privacyCheckboxMulti"
							required
							onChange={handlePolicyChange}
							data-serial-key = ""
						/>
						<label
							className="form-check-label text-xs"
							htmlFor="privacyCheckboxMulti"
							dangerouslySetInnerHTML={parsedPolicyText}
						/>
					</div>
				</div>
			</>
		);

		const renderContentStep = (step) => {
			return (
				<>
					<div className="mb-4">
						<h4 className="mb-3">{step.title}</h4>
						{step.short_desc && <p className="mb-3">{step.short_desc}</p>}
						{step.desc && (
							<div
								className="mb-4"
								dangerouslySetInnerHTML={{ __html: step.desc }}
							/>
						)}
					</div>

					<div className="py-2">
						<RenderItemsByType
							step={step}
							stepIndex={stepIndex}
							itemIndex={stepIndex}
							showValidationError={stepValidationState[stepIndex] && Object.keys(stepValidationState[stepIndex])?.length > 0  ? true : false}
						/>
					</div>
				</>
			);

		};

		const isStepVisible = currentStep === stepIndex;

		return (
			<div
				className="mb-5"
				key={stepIndex}
				style={{
					visibility: isStepVisible ? "visible" : "hidden",
					position: isStepVisible ? "static" : "absolute",
					left: isStepVisible ? "auto" : "-9999px",
					height: isStepVisible ? "auto" : "0",
					overflow: isStepVisible ? "visible" : "hidden",
				}}>
				<div data-step={stepIndex}>
					{!(stepData?.step ?? false) ? (
						<>
							{stepData.type === "introduction" && renderIntroductionStep()}
							{stepData.type === "lead" && renderLeadStep(leadContactType)}
						</>
					) : (
						<>
							{renderContentStep(step)}
						</>
					)}
					{stepIndex === totalSteps && renderPolicyStep()}
				</div>
			</div>
		);
	};

/*

<Button
	type="button"
	variant="primary"
	size="lg"
	letterCase="uppercase"
	onClick={goToNextStep}>
	Start
</Button>

*/
	const renderButtons = (currentStep) => (
		<FixedContainer
			fixedOn="md"
			position="bottom"
			className="p-4"
			title={data.title}>
			<div className="d-flex gap-3 justify-content-center w-100">
				{currentStep > 0 && (
					<Button
						type="button"
						variant="secondary"
						onClick={goToPreviousStep}
						aria-label="Gehe zum vorherigen Schritt"
						disabled={isSubmitted}>
						Zurück
					</Button>
				)}

				{currentStep < totalSteps ? (
					<>
						{currentStep > 0 ? (
							
						<Button
							type="button"
							variant="primary"
							onClick={goToNextStep}
							aria-label="Gehe zum nächsten Schritt"
							disabled={stepValidationState[currentStep] && Object.keys(stepValidationState[currentStep])?.length > 0  ? true : false}>
							Weiter
						</Button>
						) : (
							<Button
								type="button"
								variant="primary"
								size="lg"
								letterCase="uppercase"
								onClick={goToNextStep}>
								Start
							</Button>
						)}
					</>
				) : (
					<Button
						type="submit"
						variant="primary"
						letterCase="uppercase"
						aria-label="Absenden"
						disabled={isSubmitted}>
						Absenden
					</Button>
				)}
			</div>
		</FixedContainer>
	);

	// Init for active tiles
	useEffect(() => {
		const initializeActiveStatus = () => {
			const tiles = document.querySelectorAll(`.${styles.tile}`);
			tiles.forEach((tile) => {
				const input = tile.querySelector('input[type="checkbox"], input[type="radio"]');
				if (input && input.checked) {
					tile.classList.add(styles.active);
				}
			});
		};

		// Run after a short delay to ensure DOM is ready
		setTimeout(initializeActiveStatus, 100);
	}, []);

	// Handler for policy checkbox changes
	const handlePolicyChange = (e) => {
		if (e.target.checked) {
			// Remove invalid class when checked
			e.target.classList.remove("is-invalid");
		}
	};

	// Add global keyboard handlers
	useEffect(() => {
		// Handler for keyboard navigation - moved inside useEffect
		const handleKeyDown = (e) => {
			if (e.key === "Enter" && e.target.tagName !== "TEXTAREA") {
				e.preventDefault();

				// If user presses Enter on last step within a valid button, allow form submission
				if (currentStep === totalSteps && e.target.classList.contains("btn-primary")) {
					return;
				}

				// Otherwise prevent enter from submitting and navigate to next step instead
				if (currentStep < totalSteps) {
					goToNextStep();
				}
			}
		};

		document.addEventListener("keydown", handleKeyDown);

		return () => {
			document.removeEventListener("keydown", handleKeyDown);
		};
	}, [currentStep, totalSteps, goToNextStep]);


	return (
		<form
			ref={formRef}
			onSubmit={handleSubmit}
			className="needs-validation"
			noValidate>
			{/* Hidden event_id field at top level */}
			<input
				type="hidden"
				name="event_id"
				ref={(ref) => setInputRef && setInputRef("event_id", ref)}
				value="462"
				data-serialize-in-top-level="true"
				className="d-none"
			/>

			{allSteps.map((step, index) => renderStep(step, index))}

			{renderButtons(currentStep)}
		</form>
	);
};

export default MultiStepForm;
